import React from "react";
import styled from "styled-components";
import { useHTMLInputElementValidationProjection } from "../hooks/useHTMLInputElementValidationProjection";
import { useIsFocused } from "../hooks/useIsFocused";
import { Icon } from "../Icon";
import { PropError, PropLabel } from "../PropX";
import { LooseInteractionButton, LooseTextInput } from "../__Internal__";
import { calculateRealMax } from "./calculateRealMax";
import { useStepperEventHandlers } from "./useStepperEventHandlers";

type Props = {
  /**
   * The name of the stepper.
   */
  name: string;

  /**
   * The current value of the stepper.
   */
  value: number;

  onChange: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;

  /**
   * A label to display above the stepper.
   * @default undefined
   */
  label?: PropLabel;

  /**
   * The minimum value of the stepper.
   * @default 0
   */
  min?: number;

  /**
   * The maximum value of the stepper.
   * @default Infinity
   */
  max?: number;

  /**
   * The step size of the stepper.
   * @default 1
   */
  step?: number;

  /**
   * Whether the stepper is disabled.
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the stepper is required.
   * @default false
   */
  required?: boolean;

  /**
   * An error to attached to the input.
   * @default undefined
   */
  error?: PropError | PropError[];

  /**
   * The event for which to validate the input.
   * @default "blur"
   */
  validateOn?: "change" | "blur" | "manual";

  /**
   * Testing utility tag.
   * @default "compact-stepper"
   */
  "data-testid"?: string;
};

const OuterContainer = styled.div`
  display: inline-block;
  min-width: 100px;
`;

const InnerContainer = styled.div`
  all: unset;
  display: grid;
  grid-template-areas:
    "input top"
    "input bottom";
  min-width: 100px;
  max-width: 150px;
`;

const TopButtonWrapper = styled.div`
  width: 36px;
  grid-area: top;
`;

const BottomButtonWrapper = styled.div`
  width: 36px;
  grid-area: bottom;
`;

const InputWrapper = styled.div`
  grid-area: input;
`;

export const CompactStepper: React.FC<Props> = (props) => {
  const { name, value, label, disabled = false, required = false, validateOn = "blur", step = 1 } = props;
  const testId = props["data-testid"] ?? "compact-stepper";

  /**
   * Keeping track of focus is necessary to style the buttons correctly. There
   * is a divider between the buttons which _should_ change color to blue when either
   * of the buttons are clicked (because it always needs to look the boundary of the button).
   * So we keep track of focus and if either of the buttons are focused, we allow the focused button
   * to have the dividing border so that it goes blue on click.
   * (The same issue is present when either button is disabled because of max or min value, so we also check for that.)
   */
  const topRef = React.useRef<HTMLButtonElement>(null);
  const bottomRef = React.useRef<HTMLButtonElement>(null);
  const topIsFocused = useIsFocused(topRef);
  const bottomIsFocused = useIsFocused(bottomRef);

  const inputRef = React.useRef<HTMLInputElement>(null);

  /**
   * `min` must be greater or equal to zero.
   * `max` must be greater or equal to `min`.
   *
   * If `min` is less than zero, it will be set to zero.
   * If `max` is less than `min`, it will be set to `min`.
   */
  const min = Math.max(0, props.min ?? 0);
  let max = Math.max(min, Math.min(Infinity, props.max ?? Infinity));
  max = calculateRealMax(min, max, step);

  const handlers = useStepperEventHandlers({ value, min, max, step, ref: inputRef });

  const [errors, propsForInput] = useHTMLInputElementValidationProjection(props, { ref: inputRef, validateOn });
  const { invalid } = propsForInput;

  const isMin = value <= min;
  const isMax = value >= max;

  return (
    <OuterContainer>
      {label && PropLabel.renderForBeforeInput(label, { invalid, required })}
      <InnerContainer>
        <InputWrapper>
          <LooseTextInput
            ref={inputRef}
            data-testid={testId}
            type="number"
            disabled={disabled}
            name={name}
            id={name}
            value={value}
            min={min}
            max={max}
            step={step}
            required={required}
            UNSAFE_borderRadiusBottomRight="none"
            UNSAFE_borderRadiusTopRight="none"
            UNSAFE_center
            {...propsForInput}
          />
        </InputWrapper>
        <TopButtonWrapper>
          <LooseInteractionButton
            ref={topRef}
            data-testid={`${testId}/increment`}
            onClick={handlers.onClickIncrementButton}
            onMouseDown={handlers.onMouseDownIncrementButton}
            onMouseUp={handlers.onStop}
            onMouseLeave={handlers.onStop}
            disabled={disabled || isMax}
            invalid={invalid}
            borderBottom={!bottomIsFocused && (topIsFocused || !isMax)}
            borderLeft={false}
            borderRadiusTopRight="base"
          >
            <Icon icon="arrow-up" small />
          </LooseInteractionButton>
        </TopButtonWrapper>
        <BottomButtonWrapper>
          <LooseInteractionButton
            ref={bottomRef}
            data-testid={`${testId}/decrement`}
            onClick={handlers.onClickDecrementButton}
            onMouseDown={handlers.onMouseDownDecrementButton}
            onMouseUp={handlers.onStop}
            onMouseLeave={handlers.onStop}
            disabled={disabled || isMin}
            invalid={invalid}
            borderTop={bottomIsFocused || isMax}
            borderLeft={false}
            borderRadiusBottomRight="base"
          >
            <Icon icon="arrow-down" small />
          </LooseInteractionButton>
        </BottomButtonWrapper>
      </InnerContainer>
      {PropError.renderGroupAfterInput(errors)}
    </OuterContainer>
  );
};
